/**
 * History Rail — the version list model (HR-1).
 *
 * Pure and injectable: it takes a `listVersions` function rather than importing
 * `templates/auth-api.js`, so the fold arithmetic — the sharpest thing in the
 * surface being replaced — is testable in Node without a transport.
 *
 * Everything here is LIFTED from `ui/project-history-modal.js` with no
 * behaviour change, comments included, because the rules encoded in it were
 * learned the hard way and a rewrite is where they get lost.
 */

import { formatDate } from '../../shared/format.js';

/** Calendar-day bucket key (local time) for grouping. */
export function dayKeyOf(ms) {
  if (!Number.isFinite(ms)) return 'unknown';
  try {
    return new Date(ms).toDateString();
  } catch {
    return 'unknown';
  }
}

/** Human day header: Today / Yesterday / "Tue, Aug 5". */
export function dayLabelOf(ms) {
  if (!Number.isFinite(ms)) return 'Earlier';
  const now = new Date();
  const then = new Date(ms);
  if (then.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now.getTime() - 86_400_000).toDateString();
  if (then.toDateString() === yesterday) return 'Yesterday';
  try {
    return then.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      ...(then.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {})
    });
  } catch {
    return 'Earlier';
  }
}

/**
 * Absolute clock time — the row's leading value (contract §5).
 *
 * Deliberately NOT relative ("4h ago"), which the modal used. Relative time
 * cannot be compared between two rows: "3h ago" and "4h ago" are one hour apart
 * only if you read them in the same second, and the whole task on this surface
 * is comparing rows to each other. The day group already carries the date, so
 * the row only has to carry the time within it.
 */
export function clockTime(ms) {
  if (!Number.isFinite(ms)) return '—';
  try {
    return new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '—';
  }
}

export const PUBLISH_KIND_LABELS = {
  'export-html': 'HTML export',
  'export-11ty': '11ty export',
  'export-astro': 'Astro export',
  preview: 'preview link'
};

/**
 * Consecutive versions whose contentHash is NON-NULL and equal fold into the
 * newest row of the run. Pre-006 rows have null hashes and NEVER fold — a null
 * tells us nothing, and folding on it would hide real changes.
 */
export function buildRuns(versions) {
  const runs = [];
  let current = null;
  for (const v of versions) {
    const hash = v.contentHash || null;
    if (current && hash && current.hash === hash) {
      current.rows.push(v);
    } else {
      current = { hash, rows: [v] };
      runs.push(current);
    }
  }
  return runs;
}

/**
 * version → {runKey, foldedTail, foldCount, latest} for the given data.
 * `unfolded` is the set of run keys the user has expanded.
 */
export function foldInfo(versions, unfolded = new Set()) {
  const info = new Map();
  let first = true;
  for (const run of buildRuns(versions)) {
    const representative = run.rows[0];
    const runKey = `run-${representative.version}`;
    const isUnfolded = unfolded.has(runKey);
    info.set(Number(representative.version), {
      runKey,
      foldedTail: false,
      foldCount: isUnfolded ? 0 : run.rows.length - 1,
      latest: first
    });
    first = false;
    for (const v of run.rows.slice(1)) {
      info.set(Number(v.version), {
        runKey,
        foldedTail: !isUnfolded,
        foldCount: 0,
        latest: false
      });
    }
  }
  return info;
}

/**
 * @param {object} deps
 * @param {string} deps.projectId
 * @param {(id: string, opts: object) => Promise<object>} deps.listVersions
 */
export function createVersionModel({ projectId, listVersions, pageSize = 60 }) {
  let versions = [];
  let hasMore = false;
  let latestPublish = null;
  /** version → publish rows (a version can be published more than once). */
  const publishesByVersion = new Map();
  const unfoldedRuns = new Set();
  let folds = new Map();

  const notePublish = (row) => {
    if (!row || !Number.isFinite(Number(row.version))) return;
    const key = Number(row.version);
    if (!publishesByVersion.has(key)) publishesByVersion.set(key, []);
    publishesByVersion.get(key).push(row);
  };

  const resort = () => {
    versions.sort((a, b) => (b.version || 0) - (a.version || 0));
    folds = foldInfo(versions, unfoldedRuns);
  };

  return {
    get versions() {
      return versions;
    },
    get hasMore() {
      return hasMore;
    },
    get latestPublish() {
      return latestPublish;
    },

    async load({ signal } = {}) {
      const resp = await listVersions(projectId, { limit: pageSize, signal });
      versions = Array.isArray(resp?.versions) ? resp.versions : [];
      hasMore = Boolean(resp?.hasMore);
      latestPublish = resp?.latestPublish || null;
      publishesByVersion.clear();
      for (const row of resp?.publishes || []) notePublish(row);
      if (latestPublish) notePublish(latestPublish);
      resort();
      return versions;
    },

    async loadOlder() {
      const oldest = versions[versions.length - 1];
      const resp = await listVersions(projectId, {
        limit: pageSize,
        before: Number(oldest?.version)
      });
      versions = versions.concat(Array.isArray(resp?.versions) ? resp.versions : []);
      hasMore = Boolean(resp?.hasMore);
      for (const row of resp?.publishes || []) notePublish(row);
      resort();
      return versions;
    },

    foldFor(version) {
      return (
        folds.get(Number(version)) || { runKey: '', foldedTail: false, foldCount: 0, latest: false }
      );
    },

    publishesFor(version) {
      return publishesByVersion.get(Number(version)) || [];
    },

    /** A labelled version is a Milestone (contract §5); so is a published one. */
    isMilestone(v) {
      return Boolean(v?.label) || this.publishesFor(v?.version).length > 0;
    },

    /**
     * Two-way, deliberately. The modal's fold control set
     * `aria-expanded="false"` in its markup and then `.remove()`d itself on
     * click, so a screen-reader user activated a collapsed disclosure and the
     * control vanished with nothing announced — and nobody, sighted or not, had
     * a way back. A disclosure that only opens is not a disclosure.
     */
    toggleFold(runKey) {
      if (!runKey) return false;
      if (unfoldedRuns.has(runKey)) unfoldedRuns.delete(runKey);
      else unfoldedRuns.add(runKey);
      folds = foldInfo(versions, unfoldedRuns);
      return true;
    },

    isUnfolded(runKey) {
      return unfoldedRuns.has(runKey);
    },

    /** How many rows a run holds, so an expanded run can offer to re-fold. */
    runSize(runKey) {
      let n = 0;
      for (const info of folds.values()) if (info.runKey === runKey) n += 1;
      return n;
    },

    /** Rows to render: a folded tail is hidden unless its run was unfolded. */
    visibleVersions({ milestonesOnly = false } = {}) {
      return versions.filter((v) => {
        if (milestonesOnly) return this.isMilestone(v);
        return !this.foldFor(v.version).foldedTail;
      });
    },

    /**
     * "Unpublished changes since vN" — hash-aware when both hashes exist,
     * version-number fallback otherwise (pre-006 rows). No publish → no banner.
     *
     * The baseline hash rides latestPublish itself (server join): the published
     * version may be older than every loaded window, and falling back to version
     * numbers would misreport 60+ identical saves as unpublished.
     */
    publishState() {
      if (!latestPublish) return null;
      const newest = versions[0];
      if (!newest) return null;
      const publishedRow = versions.find(
        (v) => Number(v.version) === Number(latestPublish.version)
      );
      const baselineHash = latestPublish.contentHash || publishedRow?.contentHash || null;
      const bothHashed = newest.contentHash && baselineHash;
      const upToDate = bothHashed
        ? newest.contentHash === baselineHash
        : Number(newest.version) === Number(latestPublish.version);
      return {
        upToDate,
        version: Number(latestPublish.version),
        kind: PUBLISH_KIND_LABELS[latestPublish.kind] || latestPublish.kind,
        at: latestPublish.createdAt
      };
    },

    /** Tooltip detail for a version's publish chip. */
    publishDetail(version) {
      return this.publishesFor(version)
        .map((row) => `${PUBLISH_KIND_LABELS[row.kind] || row.kind} ${formatDate(row.createdAt)}`)
        .join(' · ');
    }
  };
}
